import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { tap } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { GrowthBook, InitResponse } from '@growthbook/growthbook';
import { EnvName, getParams, UaParserService } from '@hglobal/common';
import { environment } from '@pt/environment';
import { GrowthBookAttributes, GrowthBookFeatures, PersonalInfo } from '@pt/models';
import { AuthActions } from '@pt/auth';

@Injectable({ providedIn: 'root' })
export class GrowthBookService {
  readonly #uaParser = inject(UaParserService);
  readonly #actions = inject(Actions);

  readonly #hasGbKey
    = Boolean(environment.growthBook?.clientKey)
    && Boolean(environment.growthBook?.apiHost);

  readonly #actionsSub = this.#actions
    .pipe(
      ofType(AuthActions.setProfile),
      tap(({ profile }) => profile && this.#addAttributes(this.#getAttributes(profile))),
      takeUntilDestroyed(),
    )
    .subscribe();

  #growthBook = new GrowthBook<GrowthBookFeatures>({
    apiHost: this.#hasGbKey ? environment.growthBook?.apiHost! : null,
    clientKey: this.#hasGbKey ? environment.growthBook?.clientKey! : null,
    enableDevMode: environment.envName === EnvName.DEV,
    attributes: {},
    trackingCallback: (experiment, result) => {
      console.log('Experiment Viewed', {
        experimentId: experiment.key,
        variationId: result.key,
      });
    },
  });

  init(): Promise<InitResponse> {
    if (!this.#hasGbKey) {
      console.warn('[GrowthBook] No clientKey/apiHost — running in allow-all fallback mode');

      return Promise.resolve({} as unknown as InitResponse);
    }

    return this.#growthBook.init().then(result => {
      if (result?.error) {
        console.warn('GrowthBook init error:', result?.error);
      }
      return result;
    });
  }

  isOn(key: keyof GrowthBookFeatures): boolean {
    if (!this.#hasGbKey) {
      return true;
    }

    return this.#growthBook.isOn(key);
  }

  #addAttributes(attributes: GrowthBookAttributes): void {
    this.#growthBook.setAttributes({
      ...this.#growthBook.getAttributes(),
      ...attributes,
    });
  }

  #getAttributes(profile: PersonalInfo): GrowthBookAttributes {
    return getParams({
      ...profile,
      deviceType: this.#uaParser.deviceType,
      osName: this.#uaParser.osName,
    }, [
      'id',
      'email',
      'gender',
      'age',
      'birthday',
      'startDate',
      'location',
      'businessUnit',
      'service',
      'team',
      'function',
      'position',
      'status',
      'seniority',
      'workFormat',
      'deviceType',
      'osName',
    ]) as GrowthBookAttributes;
  }
}
